import { CheckIcon, SparklesIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import _ from "lodash";
import React, { useMemo } from "react";
import {
  SelectOption,
  SelectWithRecommendations,
  SimpleSelectProps,
} from "../../../common/components/input/Select";
import {
  LawfulBasis,
  ProcessingActivityDto,
} from "../../../common/service/server/dto/ProcessingActivityDto";
import {
  labelForLawfulBasis,
  useMandatoryLawfulBasisOptions,
  useRecommendedLawfulBasisOptions,
} from "../../hooks/useProcessingActivities";

const BASE_LAWFUL_BASIS_OPTIONS: LawfulBasis[] = [
  "COMPLY_WITH_LEGAL_OBLIGATIONS",
  "FULFILL_CONTRACTS",
  "LEGITIMATE_INTERESTS",
  "CONSENT",
];

export type LawfulBasisExtendedOptionsVisibility = {
  PUBLIC_INTEREST: boolean;
  VITAL_INTEREST_OF_INDIVIDUAL: boolean;
};

function LawfulBasisPillSelect({
  options,
  value,
  recommended = [],
  onChange,
}: {
  options: SelectOption[];
  value: LawfulBasis[];
  recommended?: LawfulBasis[];
  onChange: (newValue: LawfulBasis[]) => void;
}) {
  const selectedSet = useMemo(() => new Set(value), [value]);
  const recommendedSet = useMemo(() => new Set(recommended), [recommended]);

  const toggle = (optionValue: string) => {
    const basis = optionValue as LawfulBasis;
    const next = selectedSet.has(basis) ? value.filter((v) => v !== basis) : [...value, basis];
    onChange(next);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isSelected = selectedSet.has(opt.value as LawfulBasis);
        // Catalog-recommended bases (defaultOptionalLawfulBases) get a sparkle and, when
        // unselected, a purple tint — a marker only, never auto-selected (DES-30).
        const isRecommended = recommendedSet.has(opt.value as LawfulBasis);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-[12px] px-[12px]",
              "text-[14px] font-medium transition-colors",
              "border border-solid",
              isSelected
                ? "border-primary-600 bg-primary-600 text-white"
                : isRecommended
                ? "border-primary-300 bg-primary-100 text-primary-800 hover:bg-primary-200"
                : "border-neutral-500 bg-white text-neutral-800 hover:bg-neutral-50",
            )}
          >
            {isSelected && <CheckIcon className="h-4 w-4" />}
            {opt.label}
            {isRecommended && <SparklesIcon className="h-3.5 w-3.5" />}
          </button>
        );
      })}
    </div>
  );
}

export const ProcessingActivityLawfulBasisSelect: React.FC<
  {
    activity?: ProcessingActivityDto;
    value: LawfulBasis[];
    onChange: (newBases: LawfulBasis[]) => Promise<void>;
    extendedOptionsVisibility?: LawfulBasisExtendedOptionsVisibility;
    useAquiloDesign?: boolean;
  } & Partial<Omit<SimpleSelectProps, "onChange" | "options" | "variant">>
> = ({
  activity,
  value,
  onChange,
  extendedOptionsVisibility = { PUBLIC_INTEREST: false, VITAL_INTEREST_OF_INDIVIDUAL: false },
  useAquiloDesign = false,
  ...rest
}) => {
  let availableMenuOptions: LawfulBasis[] = [...BASE_LAWFUL_BASIS_OPTIONS];
  if (extendedOptionsVisibility["PUBLIC_INTEREST"]) {
    availableMenuOptions.push("PUBLIC_INTEREST");
  }
  if (extendedOptionsVisibility["VITAL_INTEREST_OF_INDIVIDUAL"]) {
    availableMenuOptions.push("VITAL_INTEREST_OF_INDIVIDUAL");
  }

  // Ensure that we always include the mandatory lawful bases in the dropdown,
  // in case an extended option is present in the mandatory items
  const finalMenuOptions: LawfulBasis[] = useMemo(
    () => _.union(availableMenuOptions, activity?.defaultMandatoryLawfulBases ?? []),
    [availableMenuOptions, activity],
  );

  const finalOptions: SelectOption[] = finalMenuOptions.map((o) => ({
    value: o,
    label: labelForLawfulBasis(o),
  }));

  const mandatoryOptions: SelectOption[] = useMandatoryLawfulBasisOptions(activity);
  const recommendedOptions: SelectOption[] = useRecommendedLawfulBasisOptions(activity);

  const handleChange = (value: any) => {
    // On autofill we get a stringified value.
    const bases: LawfulBasis[] =
      typeof value === "string" ? (value.split(",") as LawfulBasis[]) : (value as LawfulBasis[]);

    onChange(bases);
  };

  if (useAquiloDesign) {
    return (
      <LawfulBasisPillSelect
        options={finalOptions}
        value={value}
        recommended={recommendedOptions.map((o) => o.value as LawfulBasis)}
        onChange={handleChange}
      />
    );
  }

  return (
    <SelectWithRecommendations
      options={finalOptions}
      value={value}
      mandatory={mandatoryOptions}
      recommendations={recommendedOptions}
      onChange={handleChange}
      multiple
      {...rest}
    />
  );
};
